"use client";

import { Skeleton } from "@cmp-src/primitives/skeleton";
import { cn } from "@gettactic/helpers";
import React from "react";

export type ReportSkeletonKind =
  | "number"
  | "bars"
  | "trend"
  | "map"
  | "rows"
  | "strip";

interface ReportSkeletonProps {
  kind: ReportSkeletonKind;
  className?: string;
}

const BAR_WIDTHS = ["w-[92%]", "w-[71%]", "w-[58%]", "w-[44%]", "w-[27%]"];

/**
 * Placeholders shaped like what is about to render, so a loading board
 * keeps its layout instead of flashing grey text.
 */
export const ReportSkeleton: React.FC<ReportSkeletonProps> = ({
  kind,
  className
}) => {
  switch (kind) {
    case "number":
      return (
        <div className={cn("flex flex-col gap-2 py-1", className)}>
          <Skeleton className="h-9 w-28 bg-neutral-100" />
          <Skeleton className="h-3 w-40 bg-neutral-100" />
        </div>
      );
    case "bars":
      return (
        <div className={cn("flex flex-col gap-2.5 py-1", className)}>
          {BAR_WIDTHS.map((w) => (
            <div key={w} className="flex items-center gap-3">
              <Skeleton className="h-3 w-20 bg-neutral-100" />
              <Skeleton className={cn("h-4 bg-neutral-100", w)} />
            </div>
          ))}
        </div>
      );
    case "trend":
      return (
        <div
          className={cn("flex h-[300px] flex-col justify-end gap-2", className)}
        >
          <div className="flex h-full items-end gap-1.5 px-2">
            {[38, 52, 46, 70, 64, 82, 58, 74, 90, 66, 78, 84].map((h, i) => (
              <Skeleton
                // Bars stand in for the curve; heights are decoration.
                key={`${h}-${i}`}
                className="flex-1 rounded-sm bg-neutral-100"
                style={{ height: `${h}%` }}
              />
            ))}
          </div>
          <Skeleton className="h-3 w-full bg-neutral-100" />
        </div>
      );
    case "map":
      return (
        <div className={cn("flex flex-col gap-2", className)}>
          <Skeleton className="h-[420px] w-full rounded-lg bg-neutral-100" />
          <Skeleton className="h-3 w-56 bg-neutral-100" />
        </div>
      );
    case "rows":
      return (
        <div className={cn("flex flex-col gap-2 py-1", className)}>
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-7 w-full bg-neutral-100" />
          ))}
        </div>
      );
    case "strip":
      return (
        <div className={cn("grid grid-cols-3 gap-3", className)}>
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="flex items-start gap-3 rounded-xl border border-neutral-200 bg-white px-4 py-3"
            >
              <Skeleton className="mt-1 h-2.5 w-2.5 rounded-full bg-neutral-100" />
              <div className="flex flex-1 flex-col gap-2">
                <Skeleton className="h-3.5 w-[80%] bg-neutral-100" />
                <Skeleton className="h-3 w-[55%] bg-neutral-100" />
              </div>
            </div>
          ))}
        </div>
      );
  }
};
